import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, CalendarDays, CheckCircle2, ClipboardList, KeyRound,
  Clock, Award, Users, Sparkles, PlayCircle,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Footer } from "../components/Footer";
import api from "../lib/api";
import { pick, catLabel } from "../lib/pick";
import AssignmentPanel from "../components/AssignmentPanel";
import { LiveOutline, InterestForm } from "../components/LiveProgram";
import { LiveCalendar } from "../components/LiveCalendar";
import { EarlyBirdCountdown, InstructorsSection } from "../components/LiveInstructors";
import { earlyBird } from "../lib/earlyBird";
import useSeo from "../hooks/useSeo";

const fmtDate = (v, lang) => (v ? new Date(v).toLocaleDateString(lang === "en" ? "en-GB" : "el-GR", { day: "2-digit", month: "long", year: "numeric" }) : "-");

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.5 },
};

export default function ProgramPage() {
  const { courseId } = useParams();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [cases, setCases] = useState([]);
  const [enrolled, setEnrolled] = useState(false);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    api.get(`/courses/${courseId}`).then((r) => { setData(r.data); setEnrolled(!!r.data.enrolled); }).catch(() => setData(false));
    api.get(`/programs/${courseId}/case-studies`).then((r) => setCases(r.data)).catch(() => {});
  }, [courseId]);

  const buy = async () => {
    setBuying(true);
    try {
      const { data: res } = await api.post(`/programs/${courseId}/checkout`, { origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) {
      window.location.href = "/login";
    } finally { setBuying(false); }
  };

  const months = useMemo(() => {
    if (!data || !data.lessons) return [];
    const grouped = {};
    data.lessons.forEach((l) => {
      const week = l.unlock_week || 1;
      const month = Math.floor((week - 1) / 4) + 1;
      grouped[month] = grouped[month] || [];
      grouped[month].push(l);
    });
    return Object.entries(grouped)
      .map(([m, lessons]) => ({ month: Number(m), lessons }))
      .sort((a, b) => a.month - b.month);
  }, [data]);

  useSeo({
    lang,
    title: data?.course ? pick(data.course, "title", lang) : t("common.loading"),
    description:
      (data?.course && pick(data.course, "description", lang)?.replace(/\s+/g, " ").slice(0, 160)) ||
      (lang === "en"
        ? "A 6-month live sports science diploma with assignments, monthly career mentorship and a verifiable certificate."
        : "LIVE δίπλωμα αθλητικής επιστήμης 6 μηνών με εργασίες, μηνιαίο career mentorship και πιστοποιητικό με κωδικό επαλήθευσης."),
    image: data?.course?.thumbnail,
  });

  if (data === null) return <p className="max-w-7xl mx-auto px-6 py-16 text-[#475569]" data-testid="program-loading">{t("common.loading")}</p>;
  if (data === false) return <p className="max-w-7xl mx-auto px-6 py-16 text-[#DC2626]" data-testid="program-error">{t("common.error")}</p>;

  const { course, lessons } = data;
  const isLive = course.mode === "live";
  const eb = earlyBird(course);
  const highlights = isLive ? [
    { icon: CalendarDays, label: t("live.start"), value: fmtDate(course.start_date, lang) },
    { icon: Clock, label: t("live.duration"), value: t("live.durationV") },
    { icon: Users, label: t("live.seats"), value: t("live.seatsV") },
    { icon: Sparkles, label: t("live.async"), value: t("live.asyncBadge") },
    { icon: Sparkles, label: t("live.mentorship"), value: t("live.mentorshipV") },
  ] : [
    { icon: CalendarDays, label: t("sales.h1"), value: t("sales.selfPaced") },
    { icon: ClipboardList, label: t("sales.h2"), value: `${lessons.length}` },
    { icon: ClipboardList, label: t("sales.h3"), value: t("sales.h3v") },
    { icon: Award, label: t("sales.h4"), value: t("sales.h4v") },
  ];
  const installmentPlan = null;
  const benefits = isLive
    ? [t("live.b1"), t("live.b2"), t("live.b3"), t("live.b4"), t("live.b5"), t("live.b6")]
    : [t("sales.b1"), t("sales.b2"), t("sales.b3"), t("sales.b4"), t("sales.b5"), t("sales.b6")];

  return (
    <div data-testid="program-sales-page">
      {/* HERO */}
      <div className="max-w-7xl mx-auto px-5 sm:px-6 pt-6">
        <div className="relative rounded-[28px] md:rounded-[40px] overflow-hidden border border-[#1D4ED8]/30">
          {(course.cover || course.thumbnail) && <img src={course.cover || course.thumbnail} alt={course.title} className="absolute inset-0 w-full h-full object-cover opacity-30" data-testid="program-hero-cover" />}
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.97)_0%,rgba(255,255,255,0.92)_55%,rgba(238,244,255,0.75)_100%)]" />
          <div className="relative p-7 sm:p-12 lg:p-16">
            <Link data-testid="program-back-link" to="/courses" className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#475569] hover:text-[#1D4ED8] transition-colors duration-200">
              <ArrowLeft className="w-4 h-4" /> {t("course.back")}
            </Link>
            <div className="flex flex-wrap items-center gap-2 mt-6">
              <span className="badge-lime">{catLabel(course.category, lang)}</span>
              <span className="badge-outline">{t(`level.${course.level}`)}</span>
              {isLive ? (
                <>
                  <span className="badge-dark" data-testid="program-live-badge">{t("live.badge")}</span>
                  <span className="badge-outline">{t("live.start")}: {fmtDate(course.start_date, lang)}</span>
                  <span className="badge-outline">{t("live.seatsV")}</span>
                  <span className="badge-lime" data-testid="program-async-badge">{t("live.asyncBadge")}</span>
                </>
              ) : (
                <span className="badge-outline">{t("sales.selfPaced")}</span>
              )}
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl mt-6 max-w-4xl leading-[1.05] text-[#0B1B3A]" data-testid="program-title">
              {pick(course, "title", lang)}
            </h1>
            <p className="text-[#1E293B] text-base md:text-lg mt-6 max-w-2xl leading-relaxed" data-testid="program-description">{pick(course, "description", lang)}</p>

            <div className="flex flex-wrap items-end gap-8 mt-10">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#64748B]">{t("program.price")}</p>
                {isLive && eb.active ? (
                  <div className="flex items-end gap-3 mt-1" data-testid="program-earlybird-price">
                    <p className="font-heading font-black text-4xl text-[#1D4ED8]">{eb.price}€</p>
                    <p className="text-lg text-[#94A3B8] line-through mb-1">{course.price}€</p>
                    <span className="badge-lime mb-2">{t("live.ebBadge")}</span>
                  </div>
                ) : (                  <p className="font-heading font-black text-4xl text-[#1D4ED8] mt-1" data-testid="program-price">{course.price ? `${course.price}€` : "-"}</p>
                )}
                {isLive && (
                  <p className="text-sm text-[#475569] mt-2" data-testid="program-price-plan">
                    {t("live.payRegistration")} {eb.deposit}€ + {course.installments} {t("live.payInstallments")} {eb.installment}€
                  </p>
                )}
                {isLive && eb.active && (
                  <div className="mt-3"><EarlyBirdCountdown until={course.early_bird_until} /></div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-10">
              {isLive ? (
                <a data-testid="program-interest-cta" href="#interest" className="btn-blue">
                  {t("live.interestSend")} <ArrowRight className="w-4 h-4" />
                </a>
              ) : enrolled ? (
                <Link data-testid="program-continue-button" to={`/courses/${course.id}`} className="btn-blue">
                  {t("academy.continue")} <ArrowRight className="w-4 h-4" />
                </Link>
              ) : (
                <button data-testid="program-buy-button" onClick={buy} disabled={buying} className="btn-blue disabled:opacity-60">
                  {t("sales.buyNow")} · {course.price}€ <ArrowRight className="w-4 h-4" />
                </button>
              )}
              <a data-testid="program-syllabus-link" href={isLive ? "#outline" : "#syllabus"} className="btn-outline-light">{t("sales.viewSyllabus")}</a>
            </div>
          </div>
        </div>
      </div>

      {/* HIGHLIGHTS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 pt-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="program-highlights">
          {highlights.map((h, i) => {
            const Icon = h.icon;
            return (
              <motion.div key={i} {...fade} transition={{ duration: 0.45, delay: i * 0.06 }} className="card-dark p-6">
                <Icon className="w-5 h-5 text-[#1D4ED8]" />
                <p className="font-heading font-black text-2xl mt-4">{h.value}</p>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#64748B] mt-1">{h.label}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 pt-20">
        <motion.div {...fade}>
          <span className="eyebrow">
            <span className="w-7 h-px bg-[#1D4ED8]" />
            {t("sales.benefitsEyebrow")}
          </span>
          <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mt-5" data-testid="program-benefits-title">
            {t("sales.benefitsTitle")}
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {benefits.map((b, i) => (
            <motion.div key={i} {...fade} transition={{ duration: 0.45, delay: i * 0.05 }} className="card-dark p-6 flex gap-4">
              <CheckCircle2 className="w-5 h-5 text-[#1D4ED8] shrink-0 mt-0.5" />
              <p className="text-[#334155]">{b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* LIVE: mentorship + instructors TBA */}
      {isLive && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 pt-20 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <motion.div {...fade} className="card-dark p-8 lg:col-span-2" data-testid="program-async-box">
            <PlayCircle className="w-6 h-6 text-[#1D4ED8]" />
            <h3 className="font-heading font-black text-2xl mt-4">{t("live.asyncTitle")}</h3>
            <p className="text-[#475569] mt-3 max-w-3xl">{t("live.asyncText")}</p>
          </motion.div>
          <motion.div {...fade} className="card-dark p-8" data-testid="program-mentorship-box">
            <Sparkles className="w-6 h-6 text-[#1D4ED8]" />
            <h3 className="font-heading font-black text-2xl mt-4">{t("live.mentorshipTitle")}</h3>
            <p className="text-[#475569] mt-3">{t("live.mentorshipText")}</p>
            <p className="font-heading font-black text-xl text-[#1D4ED8] mt-5">{t("live.mentorshipV")}</p>
          </motion.div>
          <motion.div {...fade} transition={{ duration: 0.45, delay: 0.08 }} className="contents" data-testid="program-instructors-wrap">
            <InstructorsSection courseId={course.id} />
          </motion.div>
        </section>
      )}

      {/* LIVE: outline syllabus */}
      {isLive && course.outline?.length > 0 && <LiveOutline outline={course.outline} />}

      {/* LIVE: calendar */}
      {isLive && course.schedule?.length > 0 && <LiveCalendar schedule={course.schedule} />}

      {/* MONTH BY MONTH SYLLABUS */}
      {!isLive && (
      <section id="syllabus" className="max-w-7xl mx-auto px-5 sm:px-6 pt-20">
        <motion.div {...fade}>
          <span className="eyebrow">
            <span className="w-7 h-px bg-[#1D4ED8]" />
            {t("sales.syllabusEyebrow")}
          </span>
          <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mt-5" data-testid="program-syllabus-title">
            {isLive ? t("live.detailedTitle") : t("sales.syllabusTitle")}
          </h2>
          <p className="text-[#475569] mt-4 max-w-2xl">{isLive ? t("live.detailedSubtitle") : t("sales.syllabusSubtitle")}</p>
        </motion.div>

        {months.length === 0 ? (
          <p className="text-[#475569] mt-10" data-testid="program-syllabus-empty">{t("program.noLessons")}</p>
        ) : (
          <div className="mt-12 space-y-4" data-testid="program-syllabus">
            {months.map((m, i) => (
              <motion.div key={m.month} {...fade} transition={{ duration: 0.45, delay: Math.min(i, 6) * 0.05 }} className="card-dark p-6 sm:p-8" data-testid={`program-month-${m.month}`}>
                <div className="flex flex-wrap items-center gap-4">
                  <span className="w-12 h-12 rounded-full bg-[#1D4ED8] text-white font-heading font-black text-lg flex items-center justify-center">
                    {String(m.month).padStart(2, "0")}
                  </span>
                  <h3 className="font-heading font-black text-2xl" data-testid={`program-month-title-${m.month}`}>
                    {(() => { const mod = /^Module (\d+)\.\d+ · (.+?) — /.exec(pick(m.lessons[0] || {}, "title", lang) || ""); return mod ? `Module ${mod[1]} · ${mod[2]}` : `${t("sales.month")} ${m.month}`; })()}
                  </h3>
                  <span className="badge-outline ml-auto">{m.lessons.length} {t("courses.lessons")}</span>
                </div>
                <div className="gold-rule mt-6" />
                <div className="mt-5 space-y-3">
                  {m.lessons.map((l, li) => (
                    <div key={l.id}>
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <Sparkles className="w-4 h-4 text-[#1D4ED8] shrink-0" />
                      {enrolled ? (
                        <Link to={`/lessons/${l.id}`} data-testid={`program-lesson-link-${l.id}`} className="font-semibold flex-1 min-w-[200px] text-[#1D4ED8] hover:underline">{pick(l, "title", lang).replace(/^Module (\d+\.\d+) · .+? — /, "$1 ")}</Link>
                      ) : (
                        <span className="font-semibold flex-1 min-w-[200px]">{pick(l, "title", lang).replace(/^Module (\d+\.\d+) · .+? — /, "$1 ")}</span>
                      )}
                      <span className="text-xs text-[#64748B] inline-flex items-center gap-1">
                        <ClipboardList className="w-3 h-3" /> {l.quiz_count} {t("course.quizCount")}
                      </span>
                    </div>
                    </div>
                  ))}
                  {enrolled && cases.filter((c) => c.module === m.month).map((c) => (
                    <div key={c.title} className="mt-4 rounded-2xl border-2 border-[#1D4ED8] bg-white p-5" data-testid={`program-case-${m.month}`}>
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1D4ED8]">{t("sales.caseStudy")}</p>
                      <p className="font-heading font-black text-lg text-[#0B1B3A] mt-1">{pick(c, "title", lang)}</p>
                      <p className="text-sm text-[#475569] mt-2">{pick(c, "task", lang)}</p>
                      <div className="flex flex-wrap gap-2 mt-4">
                        {c.dataset_url && enrolled && <a href={c.dataset_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${c.dataset_url}` : c.dataset_url} className="btn-outline-light !px-4 !py-1.5 text-xs"><ClipboardList className="w-3.5 h-3.5" /> {t("sales.caseDataset")}</a>}
                      </div>
                      <AssignmentPanel courseId={courseId} module={m.month} enrolled={enrolled} />
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>
      )}

      {/* PAYMENT OPTIONS */}
      {isLive ? (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 pt-20" data-testid="live-payment-section">
          <motion.div {...fade}>
            <span className="eyebrow">
              <span className="w-7 h-px bg-[#1D4ED8]" />
              {t("pay.eyebrow")}
            </span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mt-5">{t("live.payTitle")}</h2>
            {eb.active && (
              <div className="mt-6 rounded-[22px] border-2 border-[#1D4ED8] bg-[#EEF4FF] p-6 max-w-2xl" data-testid="live-earlybird-box">
                <p className="font-heading font-black text-lg text-[#0B1B3A]">{t("live.ebTitle")}</p>
                <p className="text-sm text-[#475569] mt-2">{t("live.ebText")}</p>
              </div>
            )}
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
            <motion.div {...fade} className="card-dark p-8" data-testid="live-pay-registration">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t("live.payRegistration")}</p>
              <p className="font-heading font-black text-5xl mt-3">{eb.deposit}€</p>
              {eb.active && <p className="text-sm text-[#94A3B8] line-through mt-1">{course.deposit}€</p>}
            </motion.div>
            <motion.div {...fade} transition={{ duration: 0.45, delay: 0.06 }} className="card-cream p-8" data-testid="live-pay-installments">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#1D4ED8]">{course.installments} {t("live.payInstallments")}</p>
              <p className="font-heading font-black text-5xl mt-3">{eb.installment}€</p>
              {eb.active && <p className="text-sm text-[#94A3B8] line-through mt-1">{course.installment_amount}€</p>}
            </motion.div>
            <motion.div {...fade} transition={{ duration: 0.45, delay: 0.12 }} className="card-dark p-8" data-testid="live-pay-total">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t("live.payTotal")}</p>
              <p className="font-heading font-black text-5xl mt-3">{eb.price}€</p>
              {eb.active && <p className="text-sm text-[#94A3B8] line-through mt-1">{t("live.ebWas")}: {course.price}€</p>}
            </motion.div>
          </div>
          <p className="text-sm text-[#64748B] mt-6 max-w-2xl">{t("live.payNote")}</p>
        </section>
      ) : course.price ? (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 pt-20" data-testid="payment-section">
          <motion.div {...fade}>
            <span className="eyebrow">
              <span className="w-7 h-px bg-[#1D4ED8]" />
              {t("pay.eyebrow")}
            </span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mt-5" data-testid="payment-title">
              {t("pay.title")}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
            <motion.div {...fade} className="card-dark p-8" data-testid="payment-full">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t("pay.full")}</p>
              <p className="font-heading font-black text-5xl mt-3">{course.price}€</p>
              <p className="text-[#475569] mt-4">{t("pay.fullDesc")}</p>
            </motion.div>

            {installmentPlan && (
              <motion.div {...fade} transition={{ duration: 0.45, delay: 0.08 }} className="card-cream p-8" data-testid="payment-plan">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#1D4ED8]">{t("pay.plan")}</p>
                <p className="font-heading font-black text-5xl mt-3">
                  {t("pay.from")} {installmentPlan.monthly}€<span className="text-xl font-bold">{t("pay.monthly")}</span>
                </p>
                <div className="mt-5 space-y-2 text-[#0B1B3A]">
                  <p className="flex items-center justify-between border-b border-[#0B1B3A]/10 pb-2">
                    <span className="font-semibold">{t("pay.deposit")}</span>
                    <span className="font-heading font-black text-xl">{installmentPlan.deposit}€</span>
                  </p>
                  <p className="flex items-center justify-between">
                    <span className="font-semibold">{installmentPlan.count} {t("pay.installments")}</span>
                    <span className="font-heading font-black text-xl">{installmentPlan.count} × {installmentPlan.monthly}€</span>
                  </p>
                </div>
              </motion.div>
            )}
          </div>
          <p className="text-sm text-[#64748B] mt-6 max-w-2xl">{t("pay.note")}</p>
        </section>
      ) : null}


      {isLive && <InterestForm programTitle={course.title} />}

      {/* HOW ACCESS WORKS */}
      {!isLive && (
      <section className="max-w-7xl mx-auto px-5 sm:px-6 pt-20">
        <motion.div {...fade} className="panel-lime p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {[
            { icon: Users, title: t("sales.sp1t"), text: t("sales.sp1d") },
            { icon: KeyRound, title: t("sales.sp2t"), text: t("sales.sp2d") },
            { icon: Award, title: t("sales.s3t"), text: t("sales.s3d") },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} data-testid={`program-step-${i}`}>
                <Icon className="w-6 h-6 text-[#1D4ED8]" />
                <h3 className="font-heading font-black text-xl mt-4">{s.title}</h3>
                <p className="text-[#475569] mt-2">{s.text}</p>
              </div>
            );
          })}
        </motion.div>
      </section>
      )}

      <Footer />
    </div>
  );
}
